/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react' // eslint-disable-line @typescript-eslint/no-unused-vars
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import useScreenSize from '../../hooks/useScreenSize'
import useModules from '../../hooks/useModules'
import ExternalLink from '../ExternalLink'
import { FooterInfo, FooterLinks, FooterLogoEmpty, FooterStyles } from './Footer.styles'

const Footer: React.FC = () => {
  const {
    brand,
    footerLogo,
    footerLogoSize,
    footerCopyrightId,
    footerBackground,
    privacyPolicyLink,
    cookiePolicyLink,
    companyLink,
    siteTermsLink,
  } = useTheme()
  const { isTablet, isMobile } = useScreenSize()
  const { t } = useTranslation()
  const modules = useModules()
  let backgroundImage = footerBackground?.desktopImage
  if (isMobile) backgroundImage = footerBackground?.mobileImage
  else if (isTablet) backgroundImage = footerBackground?.tabletImage

  const currentYear = new Date().getFullYear().toString()
  const contactModule = modules.find((m) => m.name === 'contact')

  if (!contactModule) throw new Error('Missing contact module config in Footer')

  const LinkComponent = ({ children }: { children: string }) =>
    contactModule.external ? (
      <ExternalLink to={contactModule.to}>{children}</ExternalLink>
    ) : (
      <Link to={contactModule.to}>{children}</Link>
    )

  return (
    <FooterStyles
      data-testid="footer-background"
      backgroundImage={backgroundImage}
      id="footer-styles"
    >
      <FooterInfo>
        {footerLogo ? (
          <img src={footerLogo} style={{ width: footerLogoSize }} alt="Brand logo" />
        ) : (
          <FooterLogoEmpty />
        )}

        <p>
          {footerCopyrightId
            ? t(footerCopyrightId, { currentYear })
            : `White label Copyright ${currentYear}. All rights reserved.`}
        </p>
      </FooterInfo>
      <FooterLinks>
        <ul>
          <li>
            <ExternalLink to={companyLink}>{t('company')}</ExternalLink>
          </li>
          {brand !== 'demag' && (
            <li>
              <LinkComponent>{contactModule.menuItemTitle!}</LinkComponent>
            </li>
          )}
        </ul>
        <ul>
          <li>
            <ExternalLink to={siteTermsLink}>{t('site_terms')}</ExternalLink>
          </li>
          <li>
            <ExternalLink to={privacyPolicyLink}>{t('data_protection')}</ExternalLink>
          </li>
          <li>
            <ExternalLink to={cookiePolicyLink}>{t('cookie_policy')}</ExternalLink>
          </li>
        </ul>
      </FooterLinks>
    </FooterStyles>
  )
}

export default Footer
